import { useEffect, useMemo, useState } from "react";
import { LinkButton } from "./Button";
import { SectionHeader } from "./UI";
import { apiFetch } from "../lib/api";
import { renderSimpleMarkdown } from "../lib/content";

type FAQ = {
  id: number;
  question: string;
  answer: string;
  category: string;
};

export function FAQSection({
  className,
  title = "Frequently Asked Questions",
  action
}: {
  className?: string;
  title?: string;
  action?: React.ReactNode;
}) {
  const [categories, setCategories] = useState<string[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiFetch<{ categories: string[] }>("/faq/categories").catch(() => ({ categories: [] })),
      apiFetch<FAQ[]>("/faq").catch(() => [])
    ]).then(([categoryData, faqData]) => {
      if (cancelled) return;
      const nextCategories = categoryData.categories.length ? categoryData.categories : [...new Set(faqData.map((faq) => faq.category))];
      setCategories(nextCategories);
      setFaqs(faqData);
      setActiveCategory((current) => current || nextCategories[0] || "");
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => faqs.filter((faq) => !activeCategory || faq.category === activeCategory), [activeCategory, faqs]);

  return (
    <section className={["faq-page", className].filter(Boolean).join(" ")}>
      <SectionHeader
        title={title}
        className="faq-header"
        action={action || <LinkButton to="/faq" variant="ghost" size="sm">View all</LinkButton>}
      />
      <div className="faq-categories" id="faq-categories-pills">
        {categories.map((category) => (
          <button
            key={category}
            className={category === activeCategory ? "faq-pill active" : "faq-pill"}
            type="button"
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>
      <div className="faq-accordion" id="faq-accordion-container">
        {filtered.map((faq) => (
          <div key={faq.id} className={faq.id === expandedId ? "faq-item expanded" : "faq-item"}>
            <button className="faq-trigger" type="button" onClick={() => setExpandedId((value) => value === faq.id ? null : faq.id)}>
              <span className="faq-question">{faq.question}</span>
              <span className="faq-toggle-icon" />
            </button>
            <div className="faq-answer-wrapper">
              <div className="faq-answer">{renderSimpleMarkdown(faq.answer)}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
